import { Button } from '@missionlabs/smartagent-app-components'
import type { Attachment } from '@missionlabs/smartagent-service-thread'
import axios from 'axios'
import { Loader } from 'components'
import images from 'components/Editor/images'
import logger from 'logger'
import React, { useRef } from 'react'
import { getUploadURL } from 'services/api/api.thread'
import styles from './UploadMultipleAttachments.module.scss'

export interface Props {
    channel: string
    className?: string
    isLoading?: boolean
    isEditorRedesigned?: boolean
    onAttachStart: (files: File[]) => void
    onAttachEnd: (attachments: Attachment[]) => void
    onError: (message: string) => void
}

const UploadMultipleAttachments: React.FC<Props> = ({
    channel,
    className,
    isLoading,
    isEditorRedesigned,
    onAttachStart,
    onAttachEnd,
    onError,
}) => {
    const inputEl = useRef<HTMLInputElement | null>(null)

    const onChooseFile = () => {
        inputEl.current?.click()
    }

    const onHandleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
        try {
            const files = Object.values(event.target.files ?? {})

            try {
                onAttachStart(files)
            } catch (err: unknown) {
                const error = err as Error
                onError(error.message)
                return
            }

            const response = await Promise.all(
                files.map((file) => getUploadURL(channel, file.name)),
            )

            const result = await Promise.allSettled(
                response.map((r, i) => {
                    return axios.put(r.url, files[i], {
                        headers: {
                            'Content-Type': files[i].type,
                        },
                    })
                }),
            )

            const attachments = result.map<Attachment>((att, i) => {
                if (att.status === 'fulfilled') {
                    return {
                        filename: files[i].name,
                        s3Key: response[i].s3Key,
                        bucket: response[i].bucket,
                        size: files[i].size,
                        contentType: files[i].type,
                    }
                } else {
                    return {
                        filename: files[i].name,
                        s3Key: '',
                        bucket: '',
                        size: files[i].size,
                        contentType: files[i].type,
                        error: 'Upload failed',
                    }
                }
            })

            onAttachEnd(attachments)
        } catch (err: unknown) {
            const error = err as Error

            logger.error('UPLOAD-ATTACHMENT', error)
            onError('There was a problem uploading the attachments')
        } finally {
            inputEl.current!.value = ''
        }
    }

    return (
        <div className={className}>
            <Button
                type="button"
                className={isEditorRedesigned ? 'lexicalEditor-toolbar-button' : styles.uploadButton}
                styling="blank"
                title="Upload"
                onClick={onChooseFile}
            >
                <input
                    aria-label="Upload Attachment Button"
                    data-testid="upload-input"
                    disabled={isLoading}
                    ref={inputEl}
                    hidden
                    multiple
                    type="file"
                    onChange={onHandleFile}
                />
                {isLoading ? (
                    <Loader
                        loaderType="clip"
                        size={16}
                        color="#9fabb8"
                        className="no-pad no-stretch"
                    />
                ) : (
                    <img src={images.Attachment} alt="Upload Attachments" />
                )}
            </Button>
        </div>
    )
}

export default UploadMultipleAttachments
